import type { OrderType } from '@exam-management/graphql';
import { createContext, useMemo, useState, type ReactNode } from 'react';

import { useUserListSearchParams } from '../hooks';
import type { OnSelectUserCallback, UserStatus } from '../types';

export interface UserListDataGridParams {
  sort: OrderType;
  field: string;
  page: number;
  pageSize: number;
  role: string;
}

interface UserListProviderProps {
  children: ReactNode;
  initialDataGridParams: UserListDataGridParams;
  onSelectUser?: OnSelectUserCallback;
}

export interface SearchAndFilterParams {
  search?: string;
  status?: UserStatus | '';
  role?: string;
}

interface SetSearchAndFilterParams {
  (params: SearchAndFilterParams): void;
}

interface SetDataGridParams {
  (params: Partial<UserListDataGridParams>): void;
}

interface UserListProviderContextProps {
  dataGridParams: UserListDataGridParams;
  setDataGridParams: SetDataGridParams;
  searchAndFilterInput: SearchAndFilterParams;
  setSearchAndFilterInput: SetSearchAndFilterParams;
  onSelectUser?: OnSelectUserCallback;
}

export const UserListContext = createContext<UserListProviderContextProps | undefined>(undefined);

// TODO: combine UserListProvider and UserListStateProvider
export function UserListProvider({
  children,
  initialDataGridParams,
  onSelectUser,
}: Readonly<UserListProviderProps>) {
  const [searchParams, setSearchParams] = useUserListSearchParams(initialDataGridParams);

  const { field, sort, pageSize, page, search, status, role } = searchParams;

  const searchAndFilterInput = { search, status };
  const dataGridParams = { field, sort, pageSize, page, role };

  const setSearchAndFilterInput: SetSearchAndFilterParams = (params) => setSearchParams(params, {});

  const setDataGridParams: SetDataGridParams = (params) => setSearchParams(params);

  const contextValues = useMemo(
    () => ({
      searchAndFilterInput,
      setDataGridParams,
      setSearchAndFilterInput,
      dataGridParams,
      onSelectUser,
    }),
    [
      searchAndFilterInput,
      setDataGridParams,
      setSearchAndFilterInput,
      dataGridParams,
      onSelectUser,
    ],
  );

  return <UserListContext.Provider value={contextValues}>{children}</UserListContext.Provider>;
}

export function UserListStateProvider(props: Readonly<UserListProviderProps>) {
  const { children, initialDataGridParams, onSelectUser } = props;
  const [searchParams, setSearchParams] = useState<SearchAndFilterParams & UserListDataGridParams>({
    ...initialDataGridParams,
    search: '',
    status: '',
  });

  const { field, sort, pageSize, page, search, status, role } = searchParams;

  const searchAndFilterInput = { search, status };
  const dataGridParams = { field, sort, pageSize, page, role };

  const setSearchAndFilterInput: SetSearchAndFilterParams = async (params) =>
    setSearchParams((prev) => ({ ...prev, ...params }));

  const setDataGridParams: SetDataGridParams = (params) =>
    setSearchParams((prev) => ({ ...prev, ...params }));

  const contextValues = useMemo(
    () => ({
      searchAndFilterInput,
      setDataGridParams,
      setSearchAndFilterInput,
      dataGridParams,
      onSelectUser,
    }),
    [
      searchAndFilterInput,
      setDataGridParams,
      setSearchAndFilterInput,
      dataGridParams,
      onSelectUser,
    ],
  );

  return <UserListContext.Provider value={contextValues}>{children}</UserListContext.Provider>;
}
